import cls from 'classnames';
import QRCode, { QRCodeErrorCorrectionLevel } from 'qrcode';
import { useEffect, useRef, useState } from 'react';

import useResizeObserver from '@/lib/hooks/use-resize-observer';

/**
 * Samsung Browser forced dark mode clamps CSS background colours to near-black.
 * Canvas content is exempt from that filter, so the white is painted rather
 * than styled. Do not turn this back into an element with a CSS background.
 */

/** Beyond 3x the canvas would upscale with no benefit for these symbols. */
const MAX_PIXEL_RATIO = 3;

const ISO_QUIET_ZONE_MODULES = 4;

interface QrCanvasProps {
  value: string;
  alt?: string;
  errorCorrectionLevel?: QRCodeErrorCorrectionLevel;
  margin?: number;
  size?: number;
  className?: string;
  canvasClassName?: string;
}

const QrCanvas = ({
  value,
  alt,
  errorCorrectionLevel = 'M',
  margin = ISO_QUIET_ZONE_MODULES,
  size,
  className,
  canvasClassName,
}: QrCanvasProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [observedWidth] = useResizeObserver(wrapperRef);
  const [hasError, setHasError] = useState(false);

  const isFixedSize = size !== undefined;
  const renderWidth = isFixedSize ? size : observedWidth;

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || !renderWidth) {
      return undefined;
    }

    const displaySize = isFixedSize
      ? size
      : Math.round(wrapperRef.current?.clientWidth ?? renderWidth);

    if (!displaySize) {
      return undefined;
    }

    const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    const pixelSize = Math.round(displaySize * pixelRatio);

    const applyDisplaySize = () => {
      if (isFixedSize) {
        canvas.style.width = `${displaySize}px`;
        canvas.style.height = `${displaySize}px`;
      } else {
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.style.aspectRatio = '1 / 1';
      }

      canvas.style.display = 'block';
    };

    let cancelled = false;

    QRCode.toCanvas(canvas, value, {
      errorCorrectionLevel,
      margin,
      color: { dark: '#000000', light: '#ffffff' },
      width: pixelSize,
    })
      .then(() => {
        if (!cancelled) {
          applyDisplaySize();
          setHasError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [value, renderWidth, errorCorrectionLevel, margin, isFixedSize, size]);

  if (hasError) {
    return null;
  }

  const canvas = (
    <canvas
      ref={canvasRef}
      role={alt ? 'img' : undefined}
      aria-label={alt}
      className={cls('block w-full', canvasClassName)}
      style={
        isFixedSize
          ? { width: size, height: size, display: 'block' }
          : undefined
      }
    />
  );

  if (isFixedSize) {
    return canvas;
  }

  return (
    <div ref={wrapperRef} className={className}>
      {canvas}
    </div>
  );
};

export default QrCanvas;
